import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { GoogleLogo, Eye, EyeSlash } from "phosphor-react";
import { loginWithGoogle, API_URL } from "../auth";

export default function SignupPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const navigate = useNavigate();

  const handleGoogleLogin = async () => {
    // Mark this as a signup attempt for conversion tracking in OAuth callback
    sessionStorage.setItem('google_signup_intent', 'true');
    await loginWithGoogle('/dashboard');
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPasswordError("");

    // Use XMLHttpRequest to bypass platform's fetch interceptor that consumes response body
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}/auth/signup`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      setLoading(false);
      
      let result;
      try {
        result = JSON.parse(xhr.responseText);
      } catch {
        result = { message: "Failed to create account" };
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        // Google Ads signup conversion tracking
        if (window.gtag && !window.__signupTracked) {
          window.__signupTracked = true;
          window.gtag('event', 'conversion', {
            send_to: 'AW-18030156285/4sguCOqc0pIcEP2zuZVD',
            value: 1.0,
            currency: 'USD'
          });
        }
        toast.success("Account created! Welcome to Free Service Pro!");
        
        // Check for first-value onboarding redirect
        if (result.first_value_onboarding?.active && result.first_value_onboarding?.redirect_to_job) {
          navigate(`/jobs/${result.first_value_onboarding.redirect_to_job}`, { 
            state: { user: result, firstValueOnboarding: true } 
          });
        } else {
          navigate("/dashboard", { state: { user: result } });
        }
      } else {
        // Handle specific error types
        if (result.error === "PASSWORD_WEAK") {
          setPasswordError(result.message || "Password does not meet requirements.");
          setShowPasswordRequirements(true);
        } else if (result.error === "RATE_LIMITED") {
          toast.error(result.message || "Too many attempts. Please try again shortly.");
        } else if (result.error === "INVALID_REQUEST") {
          toast.error("Invalid request. Please try again.");
        } else {
          toast.error(result.detail || result.message || "Failed to create account");
        }
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      console.error("Signup network error");
      toast.error("Failed to create account. Please try again.");
    };
    
    xhr.send(JSON.stringify({
      name,
      email,
      password,
      company_name: companyName,
      website: "", // Honeypot field - always empty for real users
    }));
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
      <div className="fixed inset-0 grain-texture pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white border-2 border-zinc-200 p-8">
          <div className="text-center mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_tradespro-1/artifacts/b15t91h1_Free-Service-Pro-Logo.png"
              alt="Free Service Pro"
              className="max-w-xs w-full mx-auto object-contain"
            />
            <h1 className="font-heading text-2xl font-bold uppercase text-zinc-900 mt-4">
              Start Free Today
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Create your account in under a minute
            </p>
            <p className="text-xs text-green-600 font-medium mt-2">
              No credit card • Start using it right away
            </p>
          </div>

          {/* Google Signup - Emphasized */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full h-12 bg-white border-2 border-[#FF6600] text-zinc-900 hover:bg-orange-50 font-medium mb-4 shadow-sm"
            data-testid="google-signup-btn"
          >
            <GoogleLogo size={20} weight="bold" className="mr-2 text-[#FF6600]" />
            Start with Google (fastest)
          </Button>

          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-4 text-zinc-400">Or with email</span>
            </div>
          </div>

          {/* Email Signup */}
          <form onSubmit={handleSignup} className="space-y-3">
            <div>
              <Label htmlFor="name" className="text-zinc-700 text-sm">
                Your Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                className="mt-1 h-11 border-2 border-zinc-200 focus:border-[#FF6600]"
                required
                data-testid="signup-name-input"
              />
            </div>
            <div>
              <Label htmlFor="company" className="text-zinc-700 text-sm">
                Company Name
              </Label>
              <Input
                id="company"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Smith Plumbing LLC"
                className="mt-1 h-11 border-2 border-zinc-200 focus:border-[#FF6600]"
                required
                data-testid="signup-company-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-zinc-700 text-sm">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1 h-11 border-2 border-zinc-200 focus:border-[#FF6600]"
                required
                data-testid="signup-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-700 text-sm">
                Password <span className="text-red-500">*</span>
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) {
                      setPasswordError("");
                      setShowPasswordRequirements(false);
                    }
                  }}
                  placeholder="••••••••••••"
                  className={`pr-10 h-11 border-2 ${passwordError ? 'border-red-400' : 'border-zinc-200'} focus:border-[#FF6600]`}
                  required
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {/* Simplified password hint - ONLY shown after validation error */}
              {showPasswordRequirements && (
                <p className="mt-2 text-xs text-zinc-500" data-testid="password-hint">
                  Use 12+ characters with a mix of letters, numbers, or symbols
                </p>
              )}
              {/* Password error from backend */}
              {passwordError && (
                <p className="mt-1 text-xs text-red-500" data-testid="password-error">
                  {passwordError}
                </p>
              )}
            </div>
            {/* Honeypot field - hidden from real users, caught by bots */}
            <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                data-testid="signup-honeypot"
              />
            </div>
            
            {/* Outcome microcopy */}
            <p className="text-center text-xs text-zinc-500 pt-1">
              You'll be inside your account in under 2 minutes
            </p>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase tracking-wide"
              data-testid="signup-submit-btn"
            >
              {loading ? "Creating Account..." : "Start My Free Account →"}
            </Button>
            
            {/* Post-CTA reassurance */}
            <p className="text-center text-xs text-zinc-400">
              No credit card. No commitment.
            </p>
          </form>

          <p className="text-center text-xs text-zinc-400 mt-4">
            By signing up, you agree to our Terms and Privacy Policy
          </p>

          <p className="text-center text-sm text-zinc-500 mt-4">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-[#FF6600] font-medium hover:underline"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
